// What's on: every dated thing from every feed, in one list. GAA matches come from the
// county boards and gaa.ie; race meetings, point-to-points and horse trials from Horse
// Racing Ireland and Eventing Ireland. The trip window (DateWindow) narrows both.
//
// Row rendering is per feed — a match is "Kerry v Cork, 3:30" and a race day is
// "Galway Festival, first race 5:20" — but everything else here is feed-agnostic.
import { useMemo } from "preact/hooks";
import type { EquineEvent, EventItem, Fixture, SeasonWindow } from "../types";
import { FEEDS, FEED_ORDER, facetKey } from "../data/events";
import { LAYERS, disciplineLabel } from "../data/layers";
import {
  enabledLayers, equineData, eventBands, eventCats, eventFeeds, eventList, eventVenueName, eventsInWindow, eventsByPlace, eventsOpen,
  featuresByKey, fixturesData, listWindow, localISODate, mapFocus, select, toggleInSet,
} from "../state/store";
import { DateWindow, windowLabel } from "./DateWindow";

const FAR = "9999-12-31";

const DAY = 864e5;
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAYNAME = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function parseISO(d: string): Date { const [y, m, dd] = d.split("-").map(Number); return new Date(y, m - 1, dd); }
/** "Today, Saturday 5 September" / "Saturday 5 September" */
export function fmtDay(iso: string): string {
  const d = parseISO(iso);
  const diff = Math.round((d.getTime() - parseISO(localISODate()).getTime()) / DAY);
  const base = `${DAYNAME[d.getDay()]} ${d.getDate()} ${MONTH[d.getMonth()]}`;
  return diff === 0 ? `Today, ${base}` : diff === 1 ? `Tomorrow, ${base}` : base;
}
export function fmtShort(iso: string): string { const d = parseISO(iso); return `${DOW[d.getDay()]} ${d.getDate()} ${MON[d.getMonth()]}`; }
/** "12–14 Sep" for a run of days; empty when it is one day. */
function fmtRun(from: string, to: string): string {
  if (from === to) return "";
  const a = parseISO(from), b = parseISO(to);
  return a.getMonth() === b.getMonth()
    ? `${a.getDate()}–${b.getDate()} ${MON[a.getMonth()]}`
    : `${a.getDate()} ${MON[a.getMonth()]} – ${b.getDate()} ${MON[b.getMonth()]}`;
}
function fmtWindow(w: SeasonWindow): string {
  const a = parseISO(w.from), b = parseISO(w.to);
  if (w.from === w.to) return `${DAYNAME[a.getDay()]} ${a.getDate()} ${MONTH[a.getMonth()]} ${a.getFullYear()}`;
  if (a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()) return `${a.getDate()}–${b.getDate()} ${MONTH[a.getMonth()]} ${a.getFullYear()}`;
  return `${a.getDate()} ${MON[a.getMonth()]} – ${b.getDate()} ${MON[b.getMonth()]} ${b.getFullYear()}`;
}
const PRECISION: Record<SeasonWindow["precision"], string> = { confirmed: "", estimate: "expected", typical: "usually" };

function goTo(e: EventItem): void {
  const layer = FEEDS[e.feed].layer;
  if (e.venue.feature) {
    const feat = featuresByKey.peek().get(`${layer}:${e.venue.feature}`);
    if (feat) { select(feat, { focus: true }); return; }
  }
  if (e.venue.lat != null && e.venue.lon != null) mapFocus.value = { seq: Date.now(), lat: e.venue.lat, lon: e.venue.lon, zoom: 12 };
}

function Chip({ on, label, count, color, onClick }: { on: boolean; label: string; count?: number; color?: string; onClick: () => void }) {
  return (
    <button type="button" class={`chip ${on ? "is-on" : ""}`} style={color ? { "--chip": color } : undefined} aria-pressed={on} onClick={onClick}>
      {color && <span class="chip-dot" aria-hidden="true" />}
      {label}{count != null && <span class="chip-count">{count}</span>}
    </button>
  );
}

function VenueLine({ e, county }: { e: EventItem; county?: string | null }) {
  if (!e.venue.name) return null;
  const canGo = e.venue.lat != null && e.venue.lon != null;
  return (
    <div class="match-venue">
      {canGo ? <button type="button" class="btn-link" onClick={() => goTo(e)}>{e.venue.name}</button> : e.venue.name}
      {e.venue.approx && <span class="muted">, village shown</span>}
      {county && !e.venue.name.includes(county) && <span class="muted">, {county}</span>}
    </div>
  );
}

/**
 * On a venue's own page every row repeats that venue: "Curragh races" six times under a
 * heading that already says Curragh. Take the venue's name off the front of the title
 * and keep what is left ("day 6 of 7"), unless nothing meaningful survives.
 */
const FILLER = /^(the|racecourse|racetrack|stadium|park|house|stud|equestrian|centre|international)$/;
function localTitle(title: string, venue: string): string {
  const words = (x: string) => x.toLowerCase().replace(/[^a-z0-9\s]/g, " ").split(/\s+/).filter(Boolean);
  // "The Curragh Racecourse" is Curragh; the title "Curragh races" then starts with it.
  const v = words(venue).filter((w) => !FILLER.test(w));
  if (!v.length) return title;
  const raw = title.trim().split(/\s+/);
  let i = 0;
  for (const w of v) {
    const t = (raw[i] || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    if (t !== w) return title;
    i++;
  }
  const rest = raw.slice(i).join(" ").replace(/^[\s,–—-]+/, "");
  return rest.length > 2 ? rest[0].toUpperCase() + rest.slice(1) : title;
}

/** A GAA match. */
function MatchBody({ e, showVenue }: { e: EventItem; showVenue: boolean }) {
  const f = e.raw as Fixture;
  const isResult = f.status === "result";
  return (
    <>
      <div class="match-teams">
        {f.home} <span class="v">v</span> {f.away}
        {f.score && <span class="match-score">{f.score.home} – {f.score.away}</span>}
      </div>
      <div class="match-meta">{FEEDS.gaa.categoryLabel(f.code)}, {f.competition}{f.round ? ` — ${f.round}` : ""}</div>
      {showVenue && !isResult && <VenueLine e={e} county={f.county} />}
    </>
  );
}

/** A race meeting, a point-to-point or a horse trials. */
function EquineBody({ e, showVenue }: { e: EventItem; showVenue: boolean }) {
  const x = e.raw as EquineEvent;
  const run = fmtRun(e.date, e.endDate);
  // "Horse trials, Eventing" says one thing twice; the discipline only earns its place
  // when the kind does not already imply it.
  const IMPLIED: Record<string, string> = { horse_trials: "eventing", point_to_point: "point_to_point" };
  // The meeting is the festival a race day belongs to, or the hunt that runs a
  // point-to-point. It is dropped when the title already carries it.
  const meeting = x.meeting && !x.title.toLowerCase().includes(x.meeting.toLowerCase()) ? x.meeting : "";
  // A card can carry four Listed races; the build puts the best first, and the row shows
  // two and counts the rest rather than running to three lines of sponsor names.
  const races = (x.feature || "").split("; ").filter(Boolean);
  const feature = races.length > 2 ? `${races.slice(0, 2).join("; ")} +${races.length - 2} more` : races.join("; ");
  const meta = [
    FEEDS.equine.categoryLabel(x.kind),
    x.discipline && IMPLIED[x.kind] !== x.discipline ? disciplineLabel(x.discipline) : "",
    meeting,
    x.classes?.length ? x.classes.join(", ") : "",
  ].filter(Boolean);
  return (
    <>
      <div class="match-teams">
        {showVenue ? x.title : localTitle(x.title, e.venue.name)}
        {run && <span class="event-run">{run}</span>}
      </div>
      <div class="match-meta">{meta.join(", ")}{feature ? ` — ${feature}` : ""}</div>
      {showVenue && <VenueLine e={e} county={x.county} />}
    </>
  );
}

export function EventRow({ e, showVenue = true }: { e: EventItem; showVenue?: boolean }) {
  const f = e.feed === "gaa" ? (e.raw as Fixture) : null;
  const x = e.feed === "equine" ? (e.raw as EquineEvent) : null;
  const isResult = f?.status === "result";
  // The time column: a throw-in or first race when there is one, and for a fixture that
  // runs several days with no published start, how long it lasts.
  const days = Math.round((Date.parse(e.endDate) - Date.parse(e.date)) / DAY) + 1;
  const when = isResult ? fmtShort(e.date)
    : f?.status === "postponed" || x?.status === "cancelled" || x?.status === "abandoned" ? "Off"
    : e.time ? e.time
    : days > 1 ? `${days} days`
    // A GAA time is missing because the board has not fixed it; a racing time is simply
    // not in the feed, so "TBC" would be a claim rather than a fact.
    : f ? "TBC" : "—";
  return (
    <div class={`match feed-${e.feed} ${f?.status ?? x?.status ?? ""} ${showVenue ? "" : "no-venue"}`}>
      <div class="match-when">
        {!showVenue && !isResult && <span class="match-date">{fmtShort(e.date)}</span>}
        <b>{when}</b>
        {f?.tv && !isResult && <span class="match-tv">{f.tv}</span>}
        {x?.evening && <span class="match-tv">evening</span>}
      </div>
      <div class="match-body">
        {f ? <MatchBody e={e} showVenue={showVenue} /> : <EquineBody e={e} showVenue={showVenue} />}
      </div>
      <div class="match-links">
        {(f?.ticket_url || x?.ticket_url) && <a href={(f?.ticket_url || x?.ticket_url)!} target="_blank" rel="noopener" class="btn-ghost">Tickets</a>}
        {(f?.url || x?.url) && <a href={(f?.url || x?.url)!} target="_blank" rel="noopener" class="muted" title="Fixture on the source site">source ↗</a>}
      </div>
    </div>
  );
}

function SeasonRow({ w, layer }: { w: SeasonWindow; layer: "gaa" | "equine" }) {
  const place = w.venue ? featuresByKey.value.get(`${layer}:${w.venue}`) : null;
  return (
    <div class="season-window">
      <b>{w.title}</b>
      <span class="when">{fmtWindow(w)}{PRECISION[w.precision] ? `, ${PRECISION[w.precision]}` : ""}{place ? <>, <button type="button" class="btn-link" onClick={() => select(place, { focus: true })}>{place.name}</button></> : null}</span>
      {w.note && <p>{w.note}</p>}
    </div>
  );
}

export function Events() {
  const list = eventList.value;
  const pool = eventsInWindow.value;
  const counts = useMemo(() => {
    const feed: Record<string, number> = {}, cat: Record<string, number> = {}, band: Record<string, number> = {};
    for (const e of pool) {
      feed[e.feed] = (feed[e.feed] ?? 0) + 1;
      cat[facetKey(e.feed, e.category)] = (cat[facetKey(e.feed, e.category)] ?? 0) + 1;
      band[facetKey(e.feed, e.band)] = (band[facetKey(e.feed, e.band)] ?? 0) + 1;
    }
    return { feed, cat, band };
  }, [pool]);
  // Which feeds get their own facet rows: the ones on screen, or the one that is ticked.
  const shown = FEED_ORDER.filter((f) => counts.feed[f] && (!eventFeeds.value.size || eventFeeds.value.has(f)));
  const days: [string, EventItem[]][] = [];
  for (const e of list) { const last = days[days.length - 1]; if (last && last[0] === e.date) last[1].push(e); else days.push([e.date, [e]]); }
  const [, to] = listWindow.value;
  const soon = localISODate(new Date(Date.now() + 150 * DAY));
  // The season skeleton and the source line follow the same feeds as the list: with only
  // the Horses layer on, the shape of the GAA year is not what you are reading.
  const feedOn = (f: "gaa" | "equine") => counts.feed[f] != null || (!eventFeeds.value.size ? enabledLayers.value.has(FEEDS[f].layer) : eventFeeds.value.has(f));
  const seasons: [SeasonWindow, "gaa" | "equine"][] = [];
  const today = localISODate();
  if (feedOn("gaa")) for (const w of fixturesData.value?.season ?? []) if (w.to >= today && w.from <= soon) seasons.push([w, "gaa"]);
  if (feedOn("equine")) for (const w of equineData.value?.season ?? []) if (w.to >= today && w.from <= soon) seasons.push([w, "equine"]);
  seasons.sort((a, b) => a[0].from.localeCompare(b[0].from));
  const sources = [
    ...(feedOn("gaa") ? fixturesData.value?.sources ?? [] : []),
    ...(feedOn("equine") ? equineData.value?.sources ?? [] : []),
  ].filter((s) => s.count > 0 && !s.error).map((s) => s.name);
  const venue = eventVenueName.value;
  const loading = !fixturesData.value && !equineData.value;
  return (
    <section class="matches" aria-label="What's on">
      <header class="results-head">
        <h2>{list.length} {list.length === 1 ? "thing" : "things"} on <span class="results-sub">{windowLabel()}</span></h2>
        <button type="button" class="btn-link" onClick={() => { eventsOpen.value = false; eventVenueName.value = null; }}>Back to places</button>
      </header>
      <DateWindow />
      {FEED_ORDER.filter((f) => counts.feed[f]).length > 1 && (
        <div class="chips chips-dense">
          {FEED_ORDER.filter((f) => counts.feed[f]).map((f) => (
            <Chip key={f} on={eventFeeds.value.has(f)} label={FEEDS[f].label} count={counts.feed[f]} color={FEEDS[f].color} onClick={() => toggleInSet(eventFeeds, f)} />
          ))}
        </div>
      )}
      {shown.map((f) => {
        const cats = Object.keys(counts.cat).filter((k) => k.startsWith(`${f}:`));
        // A band that reads the same as one of the kinds above it is not a second choice:
        // "Point-to-point" is both an equine kind and its discipline, and two identical
        // chips in two rows only make the reader wonder what the difference is.
        const catLabels = new Set(cats.map((k) => FEEDS[f].categoryLabel(k.slice(f.length + 1))));
        const bands = Object.keys(counts.band)
          .filter((k) => k.startsWith(`${f}:`))
          .filter((k) => !catLabels.has(FEEDS[f].bandLabel(k.slice(f.length + 1))));
        return (
          <div class="feed-facets" key={f}>
            {cats.length > 1 && (
              <div class="chips chips-dense">
                {cats.map((k) => <Chip key={k} on={eventCats.value.has(k)} label={FEEDS[f].categoryLabel(k.slice(f.length + 1))} count={counts.cat[k]} onClick={() => toggleInSet(eventCats, k)} />)}
              </div>
            )}
            {bands.length > 1 && (
              <div class="chips chips-dense">
                {bands.map((k) => <Chip key={k} on={eventBands.value.has(k)} label={FEEDS[f].bandLabel(k.slice(f.length + 1))} count={counts.band[k]} onClick={() => toggleInSet(eventBands, k)} />)}
              </div>
            )}
          </div>
        );
      })}
      {venue && <div class="chips chips-dense"><Chip on label={`at ${venue} ×`} onClick={() => { eventVenueName.value = null; }} /></div>}
      {loading ? (
        <p class="empty">Fixtures are loading…</p>
      ) : !list.length ? (
        <p class="empty">
          Nothing published for {windowLabel()}.
          {to !== FAR ? " Try a wider window." : ""}
          {feedOn("gaa") && " County boards release club fixtures about a week ahead."}
          {feedOn("equine") && " Racing is fixed a year out, but eventing and point-to-point come a season at a time."}
        </p>
      ) : (
        days.map(([date, es]) => (
          <div class="match-day" key={date}>
            <h3>{fmtDay(date)}</h3>
            {es.map((e) => <EventRow e={e} key={e.key} />)}
          </div>
        ))
      )}
      {seasons.length > 0 && (
        <div class="season">
          <h3>The season ahead</h3>
          {seasons.map(([w, layer]) => <SeasonRow w={w} layer={layer} key={`${layer}:${w.id}`} />)}
        </div>
      )}
      {!loading && (
        <p class="hint">
          From {sources.join(", ")}.{" "}
          {feedOn("gaa") && "Club fixtures are published about a week ahead and move for weather or replays. "}
          {feedOn("equine") && "Racing dates are set a year out, but the card and the going only appear the week before, and a meeting can be abandoned for frost or waterlogging. "}
          Check the venue on the day.
        </p>
      )}
    </section>
  );
}

/** What is on at one of our own venues, for the detail view. `placeKey` is "<layer>:<id>". */
export function EventsHere({ placeKey, title = "What's on here" }: { placeKey: string; title?: string }) {
  const all = eventsByPlace.value.get(placeKey) ?? [];
  const layer = placeKey.split(":")[0] as "gaa" | "equine";
  const d = layer === "gaa" ? fixturesData.value : equineData.value;
  if (!d) return null;
  const today = localISODate();
  // Only the GAA feed carries results; an equine event is scheduled or it is off.
  const done = (e: EventItem) => e.feed === "gaa" && (e.raw as Fixture).status === "result";
  const up = all.filter((e) => !done(e) && e.endDate >= today).slice(0, 6);
  const recent = all.filter(done).sort((a, b) => b.date.localeCompare(a.date)).slice(0, 3);
  // With nothing published, fall back to the season skeleton: this venue's own windows
  // first, then the general shape of that feed's year.
  const id = placeKey.split(":")[1];
  const general = layer === "gaa" ? (w: SeasonWindow) => !w.venue && w.level === "club" : (w: SeasonWindow) => !w.venue;
  const season = up.length ? [] : (d.season ?? []).filter((w) => w.to >= today && (w.venue === id || general(w))).slice(0, 2);
  const wait = layer === "gaa" ? "Club fixtures appear about a week ahead." : "Race and eventing dates are published a season ahead; a blank here means nothing is scheduled.";
  return (
    <section class="block matches-here" style={{ "--layer": LAYERS[layer].color }}>
      <h3>{title}</h3>
      {up.length ? up.map((e) => <EventRow e={e} showVenue={false} key={e.key} />) : (
        <p class="muted">
          Nothing published here yet.
          {season.length ? ` ${season.map((w) => `${w.title} ${PRECISION[w.precision] ? PRECISION[w.precision] + " " : ""}${fmtWindow(w)}`).join("; ")}.` : ""}
          {" "}{wait}
        </p>
      )}
      {recent.length > 0 && (
        <details class="sources">
          <summary>Recent results here ({recent.length})</summary>
          {recent.map((e) => <EventRow e={e} showVenue={false} key={e.key} />)}
        </details>
      )}
      <button type="button" class="btn-link" onClick={() => { eventsOpen.value = true; eventVenueName.value = null; }}>Everything coming up →</button>
    </section>
  );
}
